import {
  AlertTriangle,
  Check,
  CheckCircle,
  ChevronDown,
  Globe05,
  HelpCircle,
  Link01,
  Loading01,
  Lock01,
  LogIn01,
} from "@untitledui/icons";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { draftShareUrl } from "@/api/merchant-draft-share";
import { type MerchantDraftItem, useDraftAuthUrls } from "@/api/workspace-merchants";
import { Button } from "@/components/base/buttons/button";
import { cx } from "@/utils/cx";

export const DRAFT_CONNECTION_MESSAGE = "unbox-merchant-draft-connection";

export type CredentialGroup = NonNullable<MerchantDraftItem["credentialGroup"]>;
type HelpCard = "login" | "no_permission" | "not_sure";

const REGIONS: { id: Exclude<CredentialGroup, "JP">; label: string }[] = [
  { id: "UK", label: "UK & EU" },
  { id: "US", label: "US" },
  { id: "SEA", label: "SEA" },
];

const CONNECTING_STEPS = ["Authorising with TikTok", "Validating permissions", "Linking your shop"];

export const CONNECTION_ERRORS: Record<
  NonNullable<MerchantDraftItem["connectionError"]>,
  { title: string; body: string }
> = {
  shop_already_added: {
    title: "This TikTok Shop is already in Unbox.",
    body: "The shop you authorised is already connected to another merchant. Authorise again with a different shop.",
  },
  no_shop_authorised: {
    title: "No shop was selected.",
    body: "TikTok didn't give us access to any shop. Authorise again and select the shop you want to add.",
  },
  connection_failed: {
    title: "We couldn't connect your shop.",
    body: "Something went wrong while checking your shop on TikTok. Try authorising again.",
  },
};

const defaultCard = (answer: MerchantDraftItem["permissionAnswer"]): HelpCard => {
  if (answer === "no") return "no_permission";
  if (answer === "not_sure") return "not_sure";
  return "login";
};

export const isTikTokConnected = (draft: Pick<MerchantDraftItem, "shopConnectedAt">) => !!draft.shopConnectedAt;

const ShareLink = ({ shareToken, beforeCopy }: { shareToken: string; beforeCopy: () => Promise<void> }) => {
  const [copied, setCopied] = useState(false);
  const url = draftShareUrl(shareToken);

  const copy = async () => {
    await beforeCopy();
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="flex items-center gap-3 rounded-lg border border-secondary bg-white p-2 pl-3.5">
      <span className="flex-1 truncate text-sm text-secondary" data-testid="draft-share-link">
        {url}
      </span>
      <Button color="secondary" size="sm" iconLeading={copied ? Check : Link01} onClick={copy}>
        {copied ? "Copied" : "Copy link"}
      </Button>
    </div>
  );
};

const HelpCardButton = ({
  icon: Icon,
  title,
  isActive,
  expandable,
  onClick,
}: {
  icon: typeof LogIn01;
  title: string;
  isActive: boolean;
  expandable?: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    aria-pressed={isActive}
    onClick={onClick}
    className={cx(
      "flex items-center gap-3 rounded-xl p-4 text-left transition-colors duration-150",
      isActive ? "bg-unbox-cream-50 ring-1 ring-inset ring-gray-900" : "bg-unbox-cream-25 hover:bg-unbox-cream-50",
    )}
  >
    <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white">
      <Icon className="size-4 text-secondary" />
    </span>
    <span className="flex-1 text-sm font-semibold text-primary">{title}</span>
    {expandable && (
      <ChevronDown
        className={cx("size-4 text-quaternary transition-transform duration-150", isActive && "rotate-180")}
      />
    )}
  </button>
);

const Notice = ({ title, children, tone }: { title: string; children: ReactNode; tone: "warning" | "neutral" }) => (
  <div
    role="alert"
    className={cx(
      "flex gap-3 rounded-xl border p-4 text-sm",
      tone === "warning"
        ? "border-warning-300 bg-warning-25 text-warning-700"
        : "border-secondary bg-gray-50 text-secondary",
    )}
  >
    <AlertTriangle className="mt-0.5 size-5 shrink-0" />
    <div className="flex flex-col gap-1">
      <p className="font-semibold">{title}</p>
      <div>{children}</div>
    </div>
  </div>
);

const ConnectionStatus = ({ activeStep }: { activeStep: number }) => {
  const connected = activeStep >= CONNECTING_STEPS.length;
  return (
    <div
      className="flex flex-col gap-4 rounded-xl border border-secondary bg-unbox-cream-25 p-5"
      aria-live="polite"
      data-testid="tiktok-connection-status"
    >
      <div className="flex items-start gap-3">
        {connected ? (
          <CheckCircle className="size-[22px] shrink-0 text-success-500" />
        ) : (
          <Loading01 className="size-[22px] shrink-0 animate-spin text-brand-800" />
        )}
        <div className="flex flex-col gap-0.5 text-sm">
          <p className="font-semibold text-primary">{connected ? "TikTok Shop connected" : "Connecting your shop"}</p>
          <p className="text-secondary">
            {connected
              ? "You're all set. Select Continue when you're ready."
              : "We're validating the access. This continues on its own – you don't need to stay on this screen."}
          </p>
        </div>
      </div>
      <div className="h-2 overflow-hidden rounded-[4px] bg-gray-neutral-200">
        <div
          className={cx(
            "h-full rounded-[4px] transition-[width] duration-300",
            connected ? "bg-success-500" : "bg-brand-800",
          )}
          style={{
            width: `${Math.max(6, (Math.min(activeStep + 1, CONNECTING_STEPS.length) / CONNECTING_STEPS.length) * 100)}%`,
          }}
        />
      </div>
      <ul className="flex flex-col gap-2.5">
        {CONNECTING_STEPS.map((label, index) => (
          <li
            key={label}
            className={cx("flex items-center gap-2.5 text-sm", index > activeStep ? "text-quaternary" : "text-primary")}
          >
            {index < activeStep && <CheckCircle className="size-4 shrink-0 text-success-500" />}
            {index === activeStep && <Loading01 className="size-4 shrink-0 animate-spin text-brand-800" />}
            {index > activeStep && (
              <span className="flex size-4 shrink-0 items-center justify-center">
                <span className="size-1.5 rounded-full bg-gray-300" />
              </span>
            )}
            {label}
          </li>
        ))}
      </ul>
    </div>
  );
};

const TIKTOK_GLYPH =
  "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z";

const LOGO_TILE =
  "flex size-[68px] shrink-0 items-center justify-center rounded-[20px] shadow-[0_10px_28px_rgba(47,34,69,0.14)]";

export const TikTokShopAccessReasons = ({ className }: { className?: string }) => (
  <div className={cx("flex flex-col gap-3 text-sm text-tertiary", className)}>
    <p>
      Unbox reads the videos creators post about your products so it can check them for the risks you chose. It also
      uses your shop's affiliate tools to message creators, manage collaborations and review sample requests, with no
      manual work in Seller Center.
    </p>
    <p>
      We don't change your products or prices, and we never place orders. You can revoke access any time in TikTok Shop
      Seller Center.
    </p>
  </div>
);

const AccessIllustration = () => (
  <div
    role="img"
    aria-label="Unbox connected to TikTok Shop, with access approved."
    className="flex min-w-0 flex-[1_1_400px] flex-col items-center gap-7 overflow-hidden rounded-2xl px-7 py-10"
    style={{ background: "linear-gradient(160deg, #fcddd6 0%, #ffefec 48%, #fffcf9 100%)" }}
  >
    <div className="flex items-center">
      <span className={cx(LOGO_TILE, "bg-white")}>
        <img src="/logomark.svg" alt="" className="size-9" />
      </span>
      <span className="relative flex w-[110px] items-center justify-center">
        <span className="absolute inset-x-2 top-1/2 border-t-2 border-dashed border-[#2F2245] opacity-35" />
        <span className="relative flex size-8 items-center justify-center rounded-full bg-[#2F2245] text-white shadow-[0_0_0_6px_#ffefec]">
          <Check className="size-4" />
        </span>
      </span>
      <span className={cx(LOGO_TILE, "bg-[#161823]")}>
        <svg viewBox="0 0 24 24" className="size-8" aria-hidden="true">
          <path fill="#fff" d={TIKTOK_GLYPH} />
        </svg>
      </span>
    </div>
    <div className="flex w-full max-w-[300px] flex-col gap-3.5 rounded-[14px] bg-white px-[18px] py-4 shadow-[0_12px_32px_rgba(47,34,69,0.12)]">
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-semibold text-primary">TikTok Shop access</span>
        <span className="rounded-full border border-success-200 bg-success-50 px-2 text-xs font-semibold text-success-700">
          Approved
        </span>
      </div>
      {["62%", "48%", "70%", "40%"].map((width) => (
        <div key={width} className="flex items-center gap-3">
          <span className="flex size-[22px] shrink-0 items-center justify-center rounded-md bg-[#2F2245] text-white">
            <Check className="size-3.5" />
          </span>
          <span className="h-2 rounded bg-[#efe6e1]" style={{ width }} />
        </div>
      ))}
    </div>
  </div>
);

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

const countryLabel = (code: string) => {
  try {
    return `${regionNames.of(code) ?? code} - ${code}`;
  } catch {
    return code;
  }
};

const ShopPicker = ({
  candidates,
  selected,
  onSelect,
  onSwitchCountry,
  error,
}: {
  candidates: MerchantDraftItem["shopCandidates"];
  selected: string | null;
  onSelect: (shopId: string) => void;
  onSwitchCountry: () => void;
  error: string | null;
}) => {
  const byCountry = new Map<string, MerchantDraftItem["shopCandidates"]>();
  for (const shop of candidates) byCountry.set(shop.region, [...(byCountry.get(shop.region) ?? []), shop]);

  return (
    <div className="flex flex-col gap-8 rounded-xl border border-secondary bg-white p-6">
      <div className="flex flex-col">
        <h2 className="text-md font-semibold text-primary">Choose a shop to connect</h2>
        <p className="text-sm text-secondary">Your TikTok account has access to more than one shop in this market</p>
      </div>
      <div className="flex flex-col gap-6">
        {[...byCountry].map(([region, shops]) => (
          <div key={region} role="radiogroup" aria-label={countryLabel(region)} className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-[0.6px] text-tertiary">{countryLabel(region)}</p>
            {shops.map((shop) => {
              const isSelected = selected === shop.shopId;
              return (
                <button
                  key={shop.shopId}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => onSelect(shop.shopId)}
                  className={cx(
                    "flex items-center gap-3 rounded-lg px-4 py-3.5 text-left transition-colors duration-100",
                    isSelected
                      ? "border-2 border-brand-900 bg-brand-50"
                      : "border border-secondary bg-white hover:bg-gray-50",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cx(
                      "size-4 shrink-0 rounded-full",
                      isSelected ? "bg-brand-900" : "border border-primary",
                    )}
                  />
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate text-sm font-semibold text-primary">{shop.name}</span>
                    <span className="truncate text-xs text-tertiary">ID: {shop.code}</span>
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {error && <p className="text-sm text-error-primary">{error}</p>}
      <button
        type="button"
        onClick={onSwitchCountry}
        className="flex items-center gap-1 self-start text-sm font-semibold text-brand-900 hover:text-brand-800"
      >
        <Globe05 className="size-5" />
        Switch country
      </button>
    </div>
  );
};

const GrantAccessCard = ({
  highlight,
  waiting,
  isOpening,
  onLogin,
}: {
  highlight: boolean;
  waiting: boolean;
  isOpening: boolean;
  onLogin: () => void;
}) => {
  const [showWhy, setShowWhy] = useState(false);

  return (
    <div
      className={cx(
        "flex flex-col rounded-xl border bg-white p-6 transition-colors duration-200",
        highlight ? "border-warning-400 ring-2 ring-warning-200" : "border-secondary",
      )}
    >
      <div className="flex flex-wrap items-start gap-8">
        <div className="flex min-w-0 flex-[1_1_320px] flex-col">
          <h2 className="text-md font-semibold text-primary">Grant Unbox access on TikTok Shop</h2>
          <ol className="mt-3 flex list-decimal flex-col gap-1.5 pl-5 text-sm text-secondary">
            <li>Log in to TikTok Shop with the shop owner's login.</li>
            <li>Review the access Unbox is asking for.</li>
            <li>Confirm.</li>
          </ol>
          <p className="mt-5 text-sm text-tertiary">
            Unbox stays connected until someone revokes access in TikTok Shop Seller Center.
          </p>
          <div className="mt-6 border-t border-secondary" />
          <button
            type="button"
            aria-expanded={showWhy}
            onClick={() => setShowWhy((current) => !current)}
            className="mt-6 flex items-center gap-2 text-left text-sm font-medium text-primary"
          >
            <img src="/logomark.svg" alt="" className="size-5 shrink-0" />
            Why Unbox needs access
            <ChevronDown
              className={cx("size-4 text-quaternary transition-transform duration-150", showWhy && "rotate-180")}
            />
          </button>
          {showWhy && <TikTokShopAccessReasons className="mt-3" />}
        </div>
        <AccessIllustration />
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-secondary pt-5">
        <p className="max-w-[46ch] text-sm text-tertiary" aria-live="polite">
          {waiting
            ? "Finish on TikTok in the pop-up that just opened. This step updates on its own once you approve."
            : "TikTok opens in a pop-up. Once you approve, it closes and this step updates on its own."}
        </p>
        <button
          type="button"
          onClick={onLogin}
          disabled={waiting || isOpening}
          className={cx(
            "flex h-10 items-center gap-2 rounded-lg px-3.5 text-sm font-semibold transition-colors duration-100",
            waiting
              ? "cursor-not-allowed border border-secondary bg-white text-disabled"
              : "bg-unbox-mint text-[#2F2245] hover:bg-[#8fe0cd]",
          )}
        >
          {waiting ? "Waiting for TikTok…" : "Log in to TikTok Shop"}
          {waiting && (
            <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          )}
        </button>
      </div>
    </div>
  );
};

export const StepConnectTikTok = ({
  draft,
  credentialGroup,
  onCredentialGroupChange,
  onSaveCredentialGroup,
  showErrors,
  onConnectionChange,
  chosenShopId,
  onChooseShop,
  chooseShopError,
  onConnectingChange,
}: {
  draft: MerchantDraftItem;
  credentialGroup: CredentialGroup | null;
  onCredentialGroupChange: (group: CredentialGroup) => void;
  onSaveCredentialGroup: () => Promise<void>;
  showErrors: boolean;
  onConnectionChange: () => void;
  chosenShopId: string | null;
  onChooseShop: (shopId: string) => void;
  chooseShopError: string | null;
  onConnectingChange: (connecting: boolean) => void;
}) => {
  const [openCard, setOpenCard] = useState<HelpCard | null>(defaultCard(draft.permissionAnswer));
  const [pendingStage, setPendingStage] = useState<number | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const [cancelled, setCancelled] = useState(false);
  const authUrls = useDraftAuthUrls();
  const popupRef = useRef<Window | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const connected = isTikTokConnected(draft);
  const choosingShop = !connected && draft.shopCandidates.length > 0;
  const connecting = pendingStage !== null && pendingStage > 0;

  useEffect(() => {
    onConnectingChange(connecting);
  }, [connecting, onConnectingChange]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.data?.type !== DRAFT_CONNECTION_MESSAGE) return;
      setPendingStage((current) => (current === null ? current : 1));
      onConnectionChange();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onConnectionChange]);

  useEffect(() => {
    if (pendingStage !== 0) return;
    const interval = setInterval(() => {
      if (!popupRef.current?.closed) return;
      setPendingStage(null);
      setCancelled(true);
    }, 1000);
    return () => clearInterval(interval);
  }, [pendingStage]);

  useEffect(() => {
    if (pendingStage !== null && pendingStage > 0 && (connected || draft.connectionError || choosingShop)) {
      setPendingStage(null);
    }
  }, [pendingStage, connected, draft.connectionError, choosingShop]);

  const authorise = async () => {
    const popup = window.open("", "unbox-tiktok-authorisation", "width=560,height=760");
    popupRef.current = popup;
    setOpenError(null);
    setCancelled(false);
    setPendingStage(0);
    try {
      await onSaveCredentialGroup();
      const url = await authUrls.mutateAsync();
      if (popup) popup.location.href = url;
      else window.location.href = url;
    } catch (err) {
      popup?.close();
      setPendingStage(null);
      setOpenError(err instanceof Error ? err.message : "Something went wrong. Try again.");
    }
  };

  const showLogin = () => {
    setOpenCard("login");
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const toggleCard = (card: HelpCard) => setOpenCard((current) => (current === card ? null : card));
  const error = draft.connectionError ? CONNECTION_ERRORS[draft.connectionError] : null;

  return (
    <div className="flex flex-col gap-6 rounded-xl border border-secondary bg-white p-8">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-primary">
            TikTok Shop region<span className="ml-1 text-error-600">*</span>
          </p>
          <p className="text-sm text-tertiary">
            Where your shop is registered. This decides which TikTok authorisation link you'll use.
          </p>
        </div>
        <div role="radiogroup" aria-label="TikTok Shop region" className="grid grid-cols-3 gap-3">
          {REGIONS.map((region) => {
            const selected = credentialGroup === region.id;
            return (
              <button
                key={region.id}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={connected && !selected}
                onClick={() => onCredentialGroupChange(region.id)}
                className={cx(
                  "flex h-11 items-center justify-center gap-2 rounded-lg border bg-white text-sm font-semibold text-primary transition-colors duration-100 disabled:cursor-not-allowed disabled:opacity-50",
                  selected && "border-brand-900 ring-1 ring-brand-900",
                  !selected && showErrors && !credentialGroup && "border-warning-400",
                  !selected && !(showErrors && !credentialGroup) && "border-primary hover:bg-gray-50",
                )}
              >
                {selected && <Check className="size-4" />}
                {region.label}
              </button>
            );
          })}
        </div>
      </div>

      {credentialGroup && connected && <ConnectionStatus activeStep={CONNECTING_STEPS.length} />}

      {credentialGroup && choosingShop && pendingStage === null && (
        <ShopPicker
          candidates={draft.shopCandidates}
          selected={chosenShopId}
          onSelect={onChooseShop}
          onSwitchCountry={authorise}
          error={chooseShopError}
        />
      )}

      {credentialGroup && connecting && <ConnectionStatus activeStep={pendingStage} />}

      {credentialGroup && !connected && !connecting && !(choosingShop && pendingStage === null) && (
        <>
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
            <HelpCardButton
              icon={LogIn01}
              title="Login below to connect"
              isActive={openCard === "login"}
              onClick={showLogin}
            />
            <HelpCardButton
              icon={Lock01}
              title="Don't have permissions?"
              isActive={openCard === "no_permission"}
              expandable
              onClick={() => toggleCard("no_permission")}
            />
            <HelpCardButton
              icon={HelpCircle}
              title="Not sure who has permissions?"
              isActive={openCard === "not_sure"}
              expandable
              onClick={() => toggleCard("not_sure")}
            />
          </div>

          {openCard === "no_permission" && (
            <div className="flex flex-col gap-3 rounded-xl bg-unbox-cream-50 p-6 text-sm text-secondary">
              <p className="font-semibold text-primary">
                Share this link with the shop's Primary account owner in TikTok Shop.
              </p>
              <ShareLink shareToken={draft.shareToken} beforeCopy={onSaveCredentialGroup} />
              <p>Once your colleague has connected this, this step will update automatically.</p>
              <p>Carry on setting up the details in the meantime.</p>
            </div>
          )}

          {openCard === "not_sure" && (
            <div className="flex flex-col gap-3 rounded-xl bg-unbox-cream-50 p-6 text-sm text-secondary">
              <p className="font-semibold text-primary">How to find out who can log in</p>
              <ul className="list-disc pl-5 flex flex-col gap-1">
                <li>In TikTok Seller Centre, open Account settings.</li>
                <li>If a login shows as Primary, that person can approve.</li>
                <li>If not, it's usually whoever set up the shop.</li>
              </ul>
              <p className="font-semibold text-primary">
                Think you've found who has permissions? Share this info with them.
              </p>
              <ShareLink shareToken={draft.shareToken} beforeCopy={onSaveCredentialGroup} />
            </div>
          )}

          {error && !cancelled && pendingStage === null && (
            <Notice title={error.title} tone="warning">
              <p>{error.body}</p>
            </Notice>
          )}

          {openError && pendingStage === null && (
            <Notice title="We couldn't open TikTok Shop." tone="warning">
              <p>{openError}</p>
            </Notice>
          )}

          {cancelled && pendingStage === null && (
            <Notice title="Authorisation cancelled on TikTok." tone="neutral">
              <p>Log in to TikTok Shop again when you're ready to continue.</p>
            </Notice>
          )}

          <div ref={cardRef}>
            <GrantAccessCard
              highlight={showErrors}
              waiting={pendingStage === 0}
              isOpening={authUrls.isPending}
              onLogin={authorise}
            />
          </div>
        </>
      )}
    </div>
  );
};

export const connectFooterMessage = (
  draft: Pick<MerchantDraftItem, "shopConnectedAt" | "shopCandidates">,
  credentialGroup: CredentialGroup | null,
  connecting: boolean,
): string => {
  if (!credentialGroup) return "Choose where your shop is registered to begin";
  if (isTikTokConnected(draft)) return "Connected. Select Continue when you're ready.";
  if (connecting) return "Connecting your shop. This continues on its own.";
  if (draft.shopCandidates.length > 0) return "Choose which shop you're adding";
  return "Authorise your TikTok Shop, or share the link and continue";
};
